export type ImpressionSection = 'interpretation' | 'riskFactors';

const SECTION_ATTRIBUTE = 'data-csi-impression-section';

const LEGACY_CONFIG: {
  [key in ImpressionSection]: { containerClass: string; headingKeyword: string };
} = {
  interpretation: {
    containerClass: 'interpretation-data-container',
    headingKeyword: 'interpretation'
  },
  riskFactors: {
    containerClass: 'risk-factors-data-container',
    headingKeyword: 'risk factors'
  }
};

export function upsertImpressionsSection(
  currentImpressions: string,
  section: ImpressionSection,
  sectionHtml: string
): string {
  const root = document.createElement('div');
  root.innerHTML = (currentImpressions || '').toString();
  removeLegacySection(root, section);

  const selector = `div[${SECTION_ATTRIBUTE}="${section}"]`;
  const existingSectionNode = root.querySelector(selector);

  if (!hasMeaningfulHtml(sectionHtml)) {
    if (existingSectionNode) existingSectionNode.remove();
    return root.innerHTML;
  }

  if (existingSectionNode) {
    (existingSectionNode as HTMLElement).innerHTML = sectionHtml;
    return root.innerHTML;
  }

  const sectionNode = root.ownerDocument.createElement('div');
  sectionNode.setAttribute(SECTION_ATTRIBUTE, section);
  sectionNode.innerHTML = sectionHtml;
  root.appendChild(sectionNode);

  return root.innerHTML;
}

export function hasImpressionsSectionContent(
  currentImpressions: string,
  section: ImpressionSection
): boolean {
  const root = document.createElement('div');
  root.innerHTML = (currentImpressions || '').toString();

  const selector = `div[${SECTION_ATTRIBUTE}="${section}"]`;
  const existingSectionNode = root.querySelector(selector);
  if (existingSectionNode && hasMeaningfulHtml((existingSectionNode as HTMLElement).innerHTML)) {
    return true;
  }

  return hasLegacySectionContent(root, section);
}

function hasMeaningfulHtml(html: string): boolean {
  if (!html) return false;
  const normalized = html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return normalized.length > 0;
}

function removeLegacySection(root: HTMLElement, section: ImpressionSection) {
  processLegacySection(root, section, true);
}

function hasLegacySectionContent(root: HTMLElement, section: ImpressionSection): boolean {
  return processLegacySection(root, section, false);
}

function getLegacyHeading(container: HTMLElement, headingKeyword: string): HTMLElement | null {
  const headingCandidate = container.previousElementSibling as HTMLElement;
  if (
    headingCandidate &&
    headingCandidate.tagName === 'H4' &&
    (headingCandidate.textContent || '').toLowerCase().includes(headingKeyword)
  ) {
    return headingCandidate;
  }

  return null;
}

function processLegacySection(
  root: HTMLElement,
  section: ImpressionSection,
  remove: boolean
): boolean {
  const { containerClass, headingKeyword } = LEGACY_CONFIG[section];
  const containers = Array.from(root.querySelectorAll(`.${containerClass}`)) as HTMLElement[];

  return containers.some(container => {
    const heading = getLegacyHeading(container, headingKeyword);

    if (remove) {
      if (heading) heading.remove();
      container.remove();
      return false;
    }

    return !!heading && hasMeaningfulHtml(container.innerHTML);
  });
}
